import { t } from "i18next";
import { isEmpty } from "lodash";
import { animated, useSpring } from "react-spring";
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow.right.svg';
import { ReactComponent as CalendarIcon } from "../../icons/calendar.svg";
import { ReactComponent as GiftIcon } from "../../icons/gift.svg";
import { ReactComponent as LeaderIcon } from "../../icons/leader.svg";
import dDayCalculate from "@pocapan/shared/utils/dDayCalculate";

interface LocationOngoingEventProps {
  eventsOngoing: any[];
  locationSiteName: string;
  locationSiteIconName: string;
  onEventClick: (locationId: string, eventId: string) => void;
  locationId: string;
}

const LocationOngoingEvent = ({
  eventsOngoing,
  locationSiteName,
  locationSiteIconName,
  onEventClick,
  locationId
}: LocationOngoingEventProps) => {
  const TEXTSHINE = useSpring({
    from: { backgroundPosition: '0% 50%' },
    to: { backgroundPosition: '100% 50%' },
    config: { duration: 3000 },
    loop: true
  });

  if (isEmpty(eventsOngoing)) {
    return null;
  }

  return (
    <>
      <div className={eventsOngoing?.length > 1 ? 'pl-[20px] pr-[20px]' : 'px-[20px]'}>
        🎉
        <animated.span
          style={{
            ...TEXTSHINE,
            fontWeight: '700',
            backgroundImage:
              'linear-gradient(to right,#7953cd 20%,#00affa 30%,#0190cd 70%,#764ada 80%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            backgroundSize: '500% auto',
            whiteSpace: 'pre-wrap',
            paddingLeft: '2px'
          }}
        >
          {t('OfflineLocationDetail.eventOngoing')}
        </animated.span>
        <div
          className={`mt-[16px] flex flex-row gap-[16px] ${
            eventsOngoing?.length > 1 ? 'overflow-x-auto hidden-scroll pr-[20px]' : ''
          }`}
        >
          {eventsOngoing.map((event) => {
            return (
              <div
                key={event.eventId}
                className={`flex flex-row items-center gap-[16px] ${
                  eventsOngoing?.length > 1 ? 'flex-shrink-0 min-w-[318px]' : ''
                }`}
              >
                <img
                  src={event.baseImageUrl}
                  alt="event_image"
                  className="w-[132px] h-[188px] rounded-lg flex-shrink-0"
                />

                <div
                  className={`flex flex-col justify-between h-[188px] ${
                    eventsOngoing?.length > 1 ? 'w-[170px]' : 'w-full'
                  }`}
                >
                  <div>
                    <div className="flex flex-row items-center text-[11px] leading-[13px] w-fit rounded-[5px] px-[4px] py-[4px] bg-surface-light dark:bg-surface-dark ">
                      <img
                        src={`/assets/icons/graphic-3d/${locationSiteIconName}.png`}
                        alt="site_type_icon"
                        className="w-[13px] h-[13px] mb-[1px] mr-[2px] inline-block"
                      />
                      <div className="text-main-light-4 dark:text-main-dark-4">
                        {locationSiteName}
                      </div>
                      <div className="mx-[4px]">{'·'}</div>
                      <div className="font-semibold text-onSecondary-light dark:text-onSecondary-dark">
                        {dDayCalculate(event.startDateString, event.endDateString)}
                      </div>
                    </div>

                    <div className="mt-[8px] mb-[10px] min-h-[48px]">
                      <div className="text-[20px] leading-[24px] font-semibold text-main-light-2 dark:text-main-dark-2 line-clamp-2 break-all whitespace-normal">
                        {event.name}
                      </div>
                    </div>

                    <div className="flex flex-col text-[13px] font-normal leading-[16px] gap-[6px] text-main-light-4 dark:text-main-dark-4">
                      <div className="flex flex-row gap-[4px] items-center">
                        <LeaderIcon className="w-[14px] h-[14px] flex-shrink-0 fill-sub-light-1 dark:fill-sub-dark-1" />
                        <div className="leading-[16px] truncate">{event?.organizerTwitterId}</div>
                      </div>

                      <div className="flex flex-row gap-[4px] items-center">
                        <GiftIcon className="mb-[1px] w-[14px] h-[14px] fill-sub-light-1 dark:fill-sub-dark-1" />
                        <div className="text-[13px] font-normal leading-[16px]">
                          {isEmpty(event.goodsNames)
                            ? t('EventInformation.emptyGoodsList')
                            : event.goodsNames.length <= 2
                              ? event.goodsNames.join(', ')
                              : t('EventInformation.goodsList', {
                                  goods: event.goodsNames.slice(0, 2).join(', '),
                                  count: event.goodsNames.length - 2
                                })}
                        </div>
                      </div>
                      <div className="flex flex-row gap-[4px] items-center">
                        <CalendarIcon className="mb-[1px] w-[14px] h-[14px] fill-sub-light-1 dark:fill-sub-dark-1" />
                        <div>
                          {event.formatedStartDateString('yyyy.MM.dd')} ~{' '}
                          {event.formatedEndDateString('MM.dd')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={
                      'flex flex-row items-center gap-[4px] cursor-pointer self-end text-[14px] leading-[17px] font-semibold text-textPrimary-light dark:text-textPrimary-dark'
                    }
                    onMouseDown={() => onEventClick(locationId, event.eventId)}
                  >
                    <span>{t('LocationOngoingEvent.eventInformation')}</span>
                    <ArrowRightIcon className="w-[14px] h-[14px] fill-textPrimary-light dark:fill-textPrimary-dark" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <hr className="mx-[20px] mt-[20px] mb-[28px] border-separator-light-1 dark:border-separator-dark-1" />
    </>
  );
};

export default LocationOngoingEvent;
